import { defineComponent as _defineComponent } from 'vue'
import { ref, Ref, computed, WritableComputedRef, PropType, onMounted } from 'vue';
import {
    ConnectFlowModelingActions,
    ConnectFlowModelingGetters,
    ConnectFlowModelingMutations,
    VueFlowNodeModel,
    VueFlowConnectionModel,
    NumberModel,
} from '@/types';
import store from '@/store';
import { QuestionMarkCircleIcon } from '@heroicons/vue/outline';
import { IconRobot } from '@tabler/icons-vue';
import { Handle, Position } from '@vue-flow/core';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { NumberService } from '@/services';

interface IVirtualAgentFormState {
    number: null | NumberModel;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TransferVirtualAgent',
  props: {
    node: {
        type: Object as PropType<VueFlowNodeModel>,
        required: true,
    },
},
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();

const props = __props;

const showForm = ref(false);
const numberOptions: Ref<NumberModel[]> = ref([]);

const nodeWritable: WritableComputedRef<VueFlowNodeModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENT_BY_ID](props.node.id),
    set: (value: VueFlowNodeModel) => {
        store.commit('flow/' + ConnectFlowModelingMutations.UPDATE_ELEMENT, {
            idx: null,
            value,
        });
    },
});

const formState: Ref<IVirtualAgentFormState> = ref({
    number: null,
});

const textValidation = t('connectFlow.node.transferVirtualAgent.validation');
const numberRequired = helpers.withMessage(textValidation, (value, state) => {
    return !!state.number;
});

const rules = computed<ValidationArgs<IVirtualAgentFormState>>(() => {
    const r: ValidationArgs<IVirtualAgentFormState> = {
        number: {
            numberRequired,
        },
    };
    return r;
});

const v$: Ref<Validation<ValidationArgs<IVirtualAgentFormState>>> = useVuelidate(rules, formState);

const nodeText = computed(() => {
    let text = t('connectFlow.node.transferVirtualAgent.empty');
    if (nodeWritable.value.data.related) {
        text = nodeWritable.value.data.related.label;
    }
    return text;
});

const sourceHandles = computed(() => {
    const connections: VueFlowConnectionModel[] =
        store.getters['flow/' + ConnectFlowModelingGetters.ELEMENTS_CONNECTIONS];
    const handles = new Set<string>();
    connections
        .filter((c: VueFlowConnectionModel) => c.source === nodeWritable.value.id)
        .forEach((c) => {
            if (c.sourceHandle) {
                handles.add(c.sourceHandle);
            }
        });
    if (handles.size === 0) {
        handles.add('hangupWithoutTransfer');
    }
    return Array.from(handles);
});

const sourceOptionHandles = computed(() =>
    sourceHandles.value.filter((h) => h !== 'hangupWithoutTransfer'),
);

const isFormCorrect = ref(true);

function removeNode() {
    store.dispatch('flow/' + ConnectFlowModelingActions.DELETE_NODE, {
        nodeId: nodeWritable.value.id,
        parentId: nodeWritable.value.parentId,
    });
}

async function onSubmit() {
    isFormCorrect.value = await v$.value.$validate();
    if (isFormCorrect.value && formState.value.number) {
        nodeWritable.value.data.related = {
            value: formState.value.number.id.toString(),
            label: formState.value.number.number,
        };
        nodeWritable.value.data.virtualAgentNumber = formState.value.number.number;
        showForm.value = false;
    }
    nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    v$.value.$reset();
}

onMounted(async () => {
    try {
        const response = await NumberService.getAll();
        numberOptions.value = response.data;
        if (nodeWritable.value.data.related?.value) {
            formState.value.number =
                numberOptions.value.find(
                    (n) => n.id.toString() === nodeWritable.value.data.related?.value,
                ) || null;
        }
        isFormCorrect.value = !!formState.value.number;
        nodeWritable.value.data.hasErrors = !isFormCorrect.value;
    } catch (error) {
        console.error('Error loading numbers:', error);
    }
});

const __returned__ = { t, props, showForm, numberOptions, nodeWritable, formState, textValidation, numberRequired, rules, v$, nodeText, sourceHandles, sourceOptionHandles, isFormCorrect, removeNode, onSubmit, get QuestionMarkCircleIcon() { return QuestionMarkCircleIcon }, get IconRobot() { return IconRobot }, get Handle() { return Handle }, get Position() { return Position } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})